import { TRANSCRIPT_REVIEW_SESSION_STATUS, localFixtureSessionPrefix } from '../constants';
import { TTranscriptReviewSession } from '../types';

export const getResponsePayload = <TValue>(value: unknown): TValue => {
  if (value && typeof value === 'object' && 'body' in value) {
    return getResponsePayload<TValue>((value as { body: unknown }).body);
  }

  return value as TValue;
};

export const normalizeSession = (value: unknown): TTranscriptReviewSession => {
  const session = getResponsePayload<TTranscriptReviewSession>(value);

  return {
    ...session,
    id: session.id ?? '',
    jobId: session.jobId ?? '',
    userId: session.userId ?? '',
    status: session.status ?? TRANSCRIPT_REVIEW_SESSION_STATUS.DRAFT,
    title: session.title ?? '',
    currentRevision: session.currentRevision ?? 0,
    draftTranscript: session.draftTranscript ?? '',
    sourceTranscript: session.sourceTranscript ?? '',
    proposals: Array.isArray(session.proposals) ? session.proposals : [],
  };
};

export const normalizeSessions = (value: unknown): TTranscriptReviewSession[] => {
  const sessions = getResponsePayload<TTranscriptReviewSession[]>(value);

  if (!Array.isArray(sessions)) {
    return [];
  }

  return sessions.map((session) => normalizeSession(session));
};

export const isLocalFixtureSession = (session: TTranscriptReviewSession | null): boolean =>
  !!session?.id.startsWith(localFixtureSessionPrefix);

export const createLocalFixtureSession = (jobId: string, transcript: string): TTranscriptReviewSession => ({
  id: `${localFixtureSessionPrefix}${Date.now()}`,
  jobId,
  userId: 'local-fixture',
  sourceVersionId: null,
  publishedVersionId: null,
  status: 'local-fixture',
  title: 'Local fixture',
  sourceTranscript: transcript,
  draftTranscript: transcript,
  currentRevision: 0,
  proposals: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const upsertSession = (
  existingSessions: TTranscriptReviewSession[],
  nextSession: TTranscriptReviewSession,
): TTranscriptReviewSession[] => {
  const existingIndex = existingSessions.findIndex((item) => item.id === nextSession.id);

  if (existingIndex === -1) {
    return [nextSession, ...existingSessions];
  }

  return existingSessions.map((item) => (item.id === nextSession.id ? nextSession : item));
};
